import { Box, Flex, Text, Tooltip } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";

type NodeVariant = "verified" | "pending" | "actionRequired";

function getVerificationState(status: string): {
  variant: NodeVariant;
  label: string;
  hint: string;
} {
  if (status === "VERIFIED") {
    return {
      variant: "verified",
      label: "Verified",
      hint: "An administrator has verified your profile. You have full verified access on the platform.",
    };
  }
  if (status === "ACTION_REQUIRED") {
    return {
      variant: "actionRequired",
      label: "Verification pending",
      hint: "Update your profile where indicated, then resubmit for verification.",
    };
  }
  if (status === "SYSTEM_VERIFIED") {
    return {
      variant: "pending",
      label: "Verification pending",
      hint: "System checks are complete. A platform administrator still needs to verify your profile.",
    };
  }
  if (status === "UNDER_REVIEW") {
    return {
      variant: "pending",
      label: "Verification pending",
      hint: "Your profile is under review. An administrator will verify it when checks are complete.",
    };
  }
  return {
    variant: "pending",
    label: "Verification pending",
    hint: "Submit your profile for verification. An administrator will verify it after review.",
  };
}

const NODE_STYLES: Record<NodeVariant, { bg: string; border: string; text: string; shadow?: string }> = {
  verified: { bg: "var(--mantine-color-green-6)", border: "var(--mantine-color-green-6)", text: "#fff" },
  pending: {
    bg: "var(--mantine-color-primary-6)",
    border: "var(--mantine-color-primary-6)",
    text: "#fff",
    shadow: "0 0 0 3px var(--mantine-color-primary-2)",
  },
  actionRequired: {
    bg: "var(--mantine-color-orange-6)",
    border: "var(--mantine-color-orange-6)",
    text: "#fff",
    shadow: "0 0 0 3px var(--mantine-color-orange-2)",
  },
};

const LABEL_COLORS: Record<NodeVariant, string> = {
  verified: "var(--mantine-color-green-8)",
  pending: "var(--mantine-color-primary-8)",
  actionRequired: "var(--mantine-color-orange-8)",
};

interface ProfileVerificationStepperProps {
  status: string;
  /** Fits in profile header; still readable */
  compact?: boolean;
  /** Horizontal alignment when compact (default center for legacy header placement). */
  align?: "left" | "center";
  /** Compact layout: row (matched profile) or column (anonymous profile). */
  compactLayout?: "row" | "column";
}

export default function ProfileVerificationStepper({
  status,
  compact = false,
  align = "center",
  compactLayout = "row",
}: ProfileVerificationStepperProps) {
  const alignLeft = compact && align === "left";
  const compactColumn = compact && compactLayout === "column";
  const { variant, label, hint } = getVerificationState(status);
  const style = NODE_STYLES[variant];

  const circle = compact ? 26 : 30;
  const borderW = 2.5;
  const checkSize = compact ? 14 : 15;
  const labelFz = compact ? 10 : 10;
  const labelMaw = compact ? 118 : 88;
  const titleFz = compact ? 10 : "xs";

  const statusCircle = (
    <Box
      w={circle}
      h={circle}
      style={{
        borderRadius: "50%",
        backgroundColor: style.bg,
        border: `${borderW}px solid ${style.border}`,
        boxShadow: style.shadow || "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "all 0.2s ease",
      }}
    >
      {variant === "verified" ? (
        <IconCheck size={checkSize} color="#fff" stroke={2.5} />
      ) : (
        <Box
          w={compact ? 8 : 9}
          h={compact ? 8 : 9}
          style={{ borderRadius: "50%", backgroundColor: style.text }}
        />
      )}
    </Box>
  );

  if (compact) {
    if (compactColumn) {
      return (
        <Box py={0} style={{ flexShrink: 0, minWidth: 0 }}>
          <Text
            fz={titleFz}
            fw={700}
            c="gray.7"
            mb={6}
            tt="uppercase"
            style={{ letterSpacing: "0.04em" }}
            ta={alignLeft ? "left" : "center"}
          >
          </Text>
          <Tooltip label={hint} position="bottom" multiline w={240} withArrow openDelay={400}>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: alignLeft ? "flex-start" : "center",
                cursor: "help",
                margin: !alignLeft ? "0 auto" : undefined,
                maxWidth: labelMaw,
              }}
            >
              {statusCircle}
              <Text
                fz={labelFz}
                fw={600}
                mt={6}
                c={LABEL_COLORS[variant]}
                ta={alignLeft ? "left" : "center"}
                maw={labelMaw}
                style={{
                  textTransform: "uppercase",
                  letterSpacing: "0.02em",
                  lineHeight: 1.25,
                  wordBreak: "break-word",
                }}
              >
                {label}
              </Text>
              {variant === "actionRequired" ? (
                <Text
                  fz={10}
                  fw={600}
                  mt={4}
                  c="orange.7"
                  ta={alignLeft ? "left" : "center"}
                  maw={labelMaw}
                  lh={1.25}
                >
                  Updates needed
                </Text>
              ) : null}
            </Box>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Tooltip label={hint} position="bottom" multiline w={240} withArrow openDelay={400}>
        <Flex
          align="center"
          gap={8}
          wrap="wrap"
          style={{
            cursor: "help",
            flexShrink: 0,
            justifyContent: alignLeft ? "flex-start" : "flex-end",
          }}
        >
          <Text
            fz={titleFz}
            fw={700}
            c="gray.7"
            tt="uppercase"
            style={{ letterSpacing: "0.04em", whiteSpace: "nowrap" }}
          >
          </Text>
          {statusCircle}
          <Text
            fz={labelFz}
            fw={600}
            c={LABEL_COLORS[variant]}
            maw={labelMaw}
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              lineHeight: 1.25,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Text>
          {variant === "actionRequired" ? (
            <Text fz={10} fw={600} c="orange.7" lh={1.25} style={{ whiteSpace: "nowrap" }}>
              Updates needed
            </Text>
          ) : null}
        </Flex>
      </Tooltip>
    );
  }

  return (
    <Box w="100%" maw="100%" py="xs" px={{ base: "xs", sm: "sm" }} style={{ flexShrink: 0, minWidth: 0 }}>
      <Text
        fz={titleFz}
        fw={700}
        c="gray.7"
        mb="sm"
        tt="uppercase"
        style={{ letterSpacing: "0.06em" }}
        ta="left"
      >
        Verification
      </Text>

      <Tooltip label={hint} position="bottom" multiline w={240} withArrow openDelay={400}>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "help",
            maxWidth: labelMaw,
          }}
        >
          {statusCircle}
          <Text
            fz={labelFz}
            fw={600}
            mt={6}
            c={LABEL_COLORS[variant]}
            ta="center"
            maw={labelMaw}
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              lineHeight: 1.25,
              wordBreak: "break-word",
            }}
          >
            {label}
          </Text>
          {variant === "actionRequired" && (
            <Text fz={9} fw={600} mt={4} c="orange.7" ta="center" maw={labelMaw} lh={1.25}>
              Updates needed
            </Text>
          )}
        </Box>
      </Tooltip>
    </Box>
  );
}
